<!-- ─── Windows: Update Downloading — bottom-right floating card ─── -->
<div
  *ngIf="winUpdateAvailable"
  class="fixed bottom-6 right-6 z-[99999] w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">

  <!-- Animated progress bar at top -->
  <div class="h-2 bg-gray-100">
    <div
      class="h-2 transition-all duration-500 ease-out"
      style="background-color: var(--primary_Bg_Button_Color);"
      [style.width.%]="winDownloadProgress ?? 5">
    </div>
  </div>

  <div class="p-4">
    <div class="flex items-start gap-3">
      <!-- Spinner -->
      <div class="mt-0.5 shrink-0">
        <svg class="w-5 h-5 animate-spin" style="color: var(--primary_Bg_Button_Color);" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
      </div>

      <div class="flex-1 min-w-0">
        <p class="text-sm font-semibold text-gray-900">
          Downloading update&hellip;
          <span *ngIf="winDownloadProgress !== null" class="font-normal" style="color: var(--primary_Text_Color);">{{ winDownloadProgress }}%</span>
        </p>
        <p class="text-xs text-gray-400 mt-0.5">
          v{{ winUpdateAvailable.version }}
          <ng-container *ngIf="winDownloadSpeed">
            &nbsp;&middot;&nbsp;{{ winDownloadSpeed }} MB/s
          </ng-container>
          <ng-container *ngIf="winDownloadEta">
            &nbsp;&middot;&nbsp;{{ formatEta(winDownloadEta) }}
          </ng-container>
        </p>
      </div>

      <button
        (click)="dismissDownloading()"
        class="shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
        title="Dismiss">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>
  </div>
</div>


<!-- ─── Windows: Update Ready — centered modal ─── -->
<ng-container *ngIf="winUpdateReady">

  <!-- Backdrop -->
  <div class="fixed inset-0 z-[99998] bg-black bg-opacity-40"></div>

  <!-- Modal card -->
  <div class="fixed inset-0 z-[99999] flex items-center justify-center p-6">
    <div class="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">

      <!-- Top gradient accent -->
      <div class="h-1.5" style="background: linear-gradient(to right, var(--primary_Bg_Button_Color), color-mix(in srgb, var(--primary_Bg_Button_Color) 60%, #ffffff));"></div>

      <div class="p-8 text-center">
        <!-- Icon -->
        <div class="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full" style="background-color: var(--primary_Bg_Button_Color);">
          <svg class="h-8 w-8" style="color: var(--primary_Bg_Btn_Text_color);" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round"
              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
        </div>

        <h2 class="text-lg font-bold text-gray-900">Ready to Install</h2>
        <p class="mt-2 text-sm text-gray-500 leading-relaxed">
          The latest update has been downloaded.<br>
          Restart the app to apply it now.
        </p>

        <div class="mt-6 flex flex-col gap-3">
          <button
            (click)="restartAndInstall()"
            class="w-full rounded-xl px-4 py-3 text-sm font-semibold transition-all"
            style="background-color: var(--primary_Bg_Button_Color); color: var(--primary_Bg_Btn_Text_color);"
            onmouseover="this.style.backgroundColor='color-mix(in srgb, var(--primary_Bg_Button_Color) 88%, black)'"
            onmouseout="this.style.backgroundColor='var(--primary_Bg_Button_Color)'">
            Restart &amp; Install
          </button>
          <button
            (click)="remindLater()"
            class="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
            Remind me in 30 min
          </button>
        </div>
      </div>
    </div>
  </div>

</ng-container>


<!-- ─── macOS: Manual Download — bottom-right card ─── -->
<div
  *ngIf="macUpdateBanner"
  class="fixed bottom-6 right-6 z-[99999] w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">

  <div class="h-1" style="background: linear-gradient(to right, var(--primary_Bg_Button_Color), color-mix(in srgb, var(--primary_Bg_Button_Color) 60%, #ffffff));"></div>

  <div class="p-4">
    <div class="flex items-start gap-3">
      <!-- Icon -->
      <div class="mt-0.5 shrink-0 flex h-8 w-8 items-center justify-center rounded-full" style="background-color: var(--primary_Bg_Button_Color);">
        <svg class="h-4 w-4" style="color: var(--primary_Bg_Btn_Text_color);" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round"
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      </div>

      <div class="flex-1 min-w-0">
        <p class="text-sm font-semibold text-gray-900">Update Available</p>
        <p class="text-xs text-gray-500 mt-0.5">
          Version <strong>{{ macUpdateBanner.version }}</strong> is ready to download
        </p>
      </div>

      <button
        (click)="dismissMacUpdateBanner()"
        class="shrink-0 text-gray-400 hover:text-gray-600 transition-colors">
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <div class="mt-3 flex gap-2">
      <button
        (click)="openDownloadLink()"
        class="flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition-colors"
        style="background-color: var(--primary_Bg_Button_Color); color: var(--primary_Bg_Btn_Text_color);"
        onmouseover="this.style.backgroundColor='color-mix(in srgb, var(--primary_Bg_Button_Color) 88%, black)'"
        onmouseout="this.style.backgroundColor='var(--primary_Bg_Button_Color)'">
        Download Now
      </button>
      <button
        (click)="dismissMacUpdateBanner()"
        class="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors">
        Later
      </button>
    </div>
  </div>
</div>


<!-- ─── Update Error Toast — bottom-right, auto-dismisses after 6s ─── -->
<div
  *ngIf="updateError"
  class="fixed bottom-6 right-6 z-[99999] w-80 bg-white rounded-2xl shadow-2xl border border-red-100 overflow-hidden">

  <div class="h-1 bg-red-500"></div>

  <div class="p-4 flex items-start gap-3">
    <div class="shrink-0 flex h-8 w-8 items-center justify-center rounded-full bg-red-50">
      <svg class="h-4 w-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round"
          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
      </svg>
    </div>

    <div class="flex-1 min-w-0">
      <p class="text-sm font-semibold text-gray-900">Update Failed</p>
      <p class="text-xs text-gray-500 mt-0.5 line-clamp-2">{{ updateError }}</p>
    </div>

    <button
      (click)="updateError = null"
      class="shrink-0 text-gray-400 hover:text-gray-600 transition-colors">
      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    </button>
  </div>
</div>


<!-- ─── Update snoozed badge — reminds user an update is waiting ─── -->
<button
  *ngIf="!winUpdateReady && remindLaterTimer"
  (click)="showUpdateNow()"
  title="Update ready — click to install"
  style="border-color: var(--primary_Bg_Button_Color); color: var(--primary_Text_Color);"
  class="fixed bottom-6 right-6 z-[99999] flex items-center gap-2 rounded-full bg-white border shadow-lg px-3 py-2 text-xs font-medium transition-colors">
  <span class="relative flex h-2 w-2">
    <span class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style="background-color: var(--primary_Bg_Button_Color);"></span>
    <span class="relative inline-flex rounded-full h-2 w-2" style="background-color: var(--primary_Bg_Button_Color);"></span>
  </span>
  Update ready
</button>

<app-env-switcher></app-env-switcher>

<router-outlet></router-outlet>
